import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import EquivalenciesVisualizer from "../components/EquivalenciesVisualizer";
import { exportToPDF } from "../utils/pdfExport";
import { useCalculations } from "../hooks/useDatabase";
import { SaveCalculationModal, HistoryViewer } from "../components/SaveCalculationModal";
import {Calculator, TrendingDown, Leaf, Car, Home, UtensilsCrossed, Download, Heart, Dog, Shirt, Wifi, GraduationCap, Save, History} from 'lucide-react';

export default function HomePage() {
  const [commuteDistance, setCommuteDistance] = useState("");
  const [commuteMode, setCommuteMode] = useState("car-gasoline");
  const [homeEnergy, setHomeEnergy] = useState("");
  const [homeSize, setHomeSize] = useState("medium");
  const [energySource, setEnergySource] = useState("grid-average");
  const [dietType, setDietType] = useState("mixed");
  const [emissions, setEmissions] = useState<number | null>(null);
  const [breakdown, setBreakdown] = useState<{commute: number; home: number; diet: number} | null>(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const { calculations, loading, fetchCalculations, saveCalculation, deleteCalculation } = useCalculations();

  useEffect(() => {
    fetchCalculations({ calculationType: 'personal' });
  }, []);

  const calculateEmissions = () => {
    // Commute emissions calculation with detailed factors
    const commute = parseFloat(commuteDistance) || 0;
    const commuteFactors: Record<string, number> = {
      "car-gasoline": 0.404, // kg CO2 per mile
      "car-diesel": 0.411,
      "car-hybrid": 0.275,
      "car-electric": 0.180, // assuming US grid average
      "bus": 0.089,
      "train": 0.041,
      "motorcycle": 0.280,
      "carpool": 0.202, // half of single occupancy
    };
    const commuteEmissions = (commute * commuteFactors[commuteMode] * 260) / 1000; // 260 working days

    // Home energy calculation with size and source factors
    const energy = parseFloat(homeEnergy) || 0;
    const sizeMultipliers: Record<string, number> = {
      "small": 0.8,
      "medium": 1.0,
      "large": 1.3,
      "very-large": 1.6,
    };
    const sourceFactors: Record<string, number> = {
      "grid-average": 0.92, // kg CO2 per kWh (US average)
      "coal-heavy": 1.2,
      "natural-gas": 0.6,
      "renewable": 0.1,
      "solar": 0.05,
    };
    const homeEmissions = (energy * sizeMultipliers[homeSize] * sourceFactors[energySource] * 12) / 1000;

    // Diet emissions (annual tons CO2e)
    const dietEmissions: Record<string, number> = {
      "vegan": 1.5,
      "vegetarian": 1.7,
      "pescatarian": 2.0,
      "low-meat": 2.5,
      "mixed": 3.3,
      "high-meat": 4.5,
    };
    const dietValue = dietEmissions[dietType];

    const total = commuteEmissions + homeEmissions + dietValue;
    setEmissions(parseFloat(total.toFixed(2)));
    setBreakdown({
      commute: parseFloat(commuteEmissions.toFixed(2)),
      home: parseFloat(homeEmissions.toFixed(2)),
      diet: parseFloat(dietValue.toFixed(2)),
    });
  };

  const treesNeeded = emissions ? Math.round(emissions * 16) : 0;
  const milesDriven = emissions ? Math.round(emissions * 2485) : 0;

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        "@id": "https://www.greenhousegascalculator.com/#website",
        "url": "https://www.greenhousegascalculator.com/",
        "name": "Greenhouse Gas Calculator",
        "description": "Free personal greenhouse gas calculator and carbon footprint measurement tools",
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://www.greenhousegascalculator.com/?s={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      },
      {
        "@type": "WebApplication",
        "name": "Free Personal Greenhouse Gas Calculator",
        "url": "https://www.greenhousegascalculator.com/",
        "applicationCategory": "UtilityApplication",
        "operatingSystem": "Any",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD"
        },
        "description": "Calculate your carbon footprint instantly with inputs for daily commute, home energy, and diet. See greenhouse gas equivalencies without sign-up.",
        "featureList": "Personal emissions calculator, Daily commute calculator, Home energy calculator, Diet emissions calculator, CO2 equivalencies calculator"
      },
      {
        "@type": "Organization",
        "@id": "https://www.greenhousegascalculator.com/#organization",
        "name": "Greenhouse Gas Calculator",
        "url": "https://www.greenhousegascalculator.com/",
        "logo": {
          "@type": "ImageObject",
          "url": "https://www.greenhousegascalculator.com/logo.png"
        }
      },
      {
        "@type": "HowTo",
        "name": "How to Calculate Your Personal Carbon Footprint",
        "description": "Step-by-step guide to calculating your greenhouse gas emissions",
        "step": [
          {
            "@type": "HowToStep",
            "name": "Enter Daily Commute",
            "text": "Input your daily commute distance and select your vehicle type"
          },
          {
            "@type": "HowToStep",
            "name": "Add Home Energy Usage",
            "text": "Enter your monthly electricity usage in kWh"
          },
          {
            "@type": "HowToStep",
            "name": "Select Diet Type",
            "text": "Choose your dietary preferences to calculate food-related emissions"
          },
          {
            "@type": "HowToStep",
            "name": "View Results",
            "text": "See your total annual emissions and equivalencies like trees planted"
          }
        ]
      }
    ]
  };

  return (
    <div className="bg-white">
      <SEO
        title="Free Personal Greenhouse Gas Calculator | Calculate Your Carbon Footprint"
        description="Free greenhouse gas calculator for individuals and businesses. Instantly measure your carbon footprint with our easy-to-use tools. No sign-up required. Calculate emissions from daily commute, home energy, and lifestyle."
        keywords="greenhouse gas calculator, carbon footprint calculator, personal emissions calculator, GHG calculator, CO2 calculator, greenhouse gas reduction calculator, greenhouse gases equivalencies calculator"
        canonical="https://www.greenhousegascalculator.com/"
        schema={schema}
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-green-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Free Personal Greenhouse Gas Calculator
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Instantly measure your emissions and see the Greenhouse Gases Equivalencies – No sign-up required.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={() => document.getElementById("calculator")?.scrollIntoView({ behavior: "smooth" })}
                className="bg-[#2E8B57] text-white px-8 py-3 rounded-lg font-medium hover:bg-[#267347] transition-colors"
              >
                Calculate My Footprint
              </button>
              <button
                onClick={() => document.getElementById("equivalencies")?.scrollIntoView({ behavior: "smooth" })}
                className="bg-white text-[#2E8B57] px-8 py-3 rounded-lg font-medium border-2 border-[#2E8B57] hover:bg-green-50 transition-colors"
              >
                See My Savings
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Calculator Widget */}
      <section id="calculator" className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gray-50 rounded-2xl p-8 shadow-sm border border-gray-200">
            <div className="flex items-center gap-3 mb-8">
              <div className="bg-[#2E8B57] p-3 rounded-lg">
                <Calculator className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">
                Calculate Your Carbon Footprint
              </h2>
            </div>

            <div className="space-y-6 mb-8">
              {/* Daily Commute */}
              <div className="bg-white p-6 rounded-xl border border-gray-200">
                <div className="flex items-center gap-2 mb-4">
                  <Car className="w-5 h-5 text-[#2E8B57]" />
                  <label className="font-semibold text-gray-900">
                    Daily Commute
                  </label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-600 mb-1 block">Transportation Mode</label>
                    <select
                      value={commuteMode}
                      onChange={(e) => setCommuteMode(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E8B57] focus:border-transparent"
                    >
                      <option value="car-gasoline">Car (Gasoline)</option>
                      <option value="car-diesel">Car (Diesel)</option>
                      <option value="car-hybrid">Car (Hybrid)</option>
                      <option value="car-electric">Car (Electric)</option>
                      <option value="carpool">Carpool (2+ people)</option>
                      <option value="bus">Bus</option>
                      <option value="train">Train/Subway</option>
                      <option value="motorcycle">Motorcycle</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 mb-1 block">Round-trip Distance</label>
                    <input
                      type="number"
                      value={commuteDistance}
                      onChange={(e) => setCommuteDistance(e.target.value)}
                      placeholder="Miles per day"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E8B57] focus:border-transparent"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Based on 260 working days per year
                </p>
              </div>

              {/* Home Energy */}
              <div className="bg-white p-6 rounded-xl border border-gray-200">
                <div className="flex items-center gap-2 mb-4">
                  <Home className="w-5 h-5 text-[#2E8B57]" />
                  <label className="font-semibold text-gray-900">
                    Home Energy
                  </label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm text-gray-600 mb-1 block">Monthly kWh</label>
                    <input
                      type="number"
                      value={homeEnergy}
                      onChange={(e) => setHomeEnergy(e.target.value)}
                      placeholder="e.g., 900"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E8B57] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 mb-1 block">Home Size</label>
                    <select
                      value={homeSize}
                      onChange={(e) => setHomeSize(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E8B57] focus:border-transparent"
                    >
                      <option value="small">Small (&lt;1000 sq ft)</option>
                      <option value="medium">Medium (1000-2000 sq ft)</option>
                      <option value="large">Large (2000-3000 sq ft)</option>
                      <option value="very-large">Very Large (3000+ sq ft)</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 mb-1 block">Energy Source</label>
                    <select
                      value={energySource}
                      onChange={(e) => setEnergySource(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E8B57] focus:border-transparent"
                    >
                      <option value="grid-average">Grid Average</option>
                      <option value="coal-heavy">Coal Heavy</option>
                      <option value="natural-gas">Natural Gas</option>
                      <option value="renewable">Renewable Mix</option>
                      <option value="solar">Solar/Wind</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Diet */}
              <div className="bg-white p-6 rounded-xl border border-gray-200">
                <div className="flex items-center gap-2 mb-4">
                  <UtensilsCrossed className="w-5 h-5 text-[#2E8B57]" />
                  <label className="font-semibold text-gray-900">Diet</label>
                </div>
                <select
                  value={dietType}
                  onChange={(e) => setDietType(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E8B57] focus:border-transparent"
                >
                  <option value="vegan">Vegan</option>
                  <option value="vegetarian">Vegetarian</option>
                  <option value="pescatarian">Pescatarian</option>
                  <option value="low-meat">Low Meat (1-2x/week)</option>
                  <option value="mixed">Mixed (Regular Meat)</option>
                  <option value="high-meat">High Meat (Daily)</option>
                </select>
                <p className="text-xs text-gray-500 mt-2">
                  Your typical eating habits and frequency
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={calculateEmissions}
                className="flex-1 bg-[#2E8B57] text-white py-4 rounded-lg font-semibold hover:bg-[#267347] transition-colors"
              >
                Calculate Emissions
              </button>
              <button
                onClick={() => setShowHistory(true)}
                className="px-6 bg-gray-100 text-gray-700 py-4 rounded-lg font-semibold hover:bg-gray-200 transition-colors flex items-center gap-2"
                title="View saved calculations"
              >
                <History className="w-5 h-5" />
                History
              </button>
            </div>

            {emissions !== null && breakdown && (
              <div className="mt-8 space-y-4">
                <div className="bg-white p-6 rounded-xl border-2 border-[#2E8B57]">
                  <div className="text-center">
                    <p className="text-gray-600 mb-2">Your Annual Carbon Footprint</p>
                    <p className="text-4xl font-bold text-[#2E8B57]">
                      {emissions} tons CO₂e
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      per year from these activities
                    </p>
                  </div>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-xl">
                  <h3 className="font-semibold text-gray-900 mb-4">Emissions Breakdown</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Commute</span>
                      <span className="font-semibold text-gray-900">{breakdown.commute} tons ({Math.round((breakdown.commute/emissions)*100)}%)</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Home Energy</span>
                      <span className="font-semibold text-gray-900">{breakdown.home} tons ({Math.round((breakdown.home/emissions)*100)}%)</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Diet</span>
                      <span className="font-semibold text-gray-900">{breakdown.diet} tons ({Math.round((breakdown.diet/emissions)*100)}%)</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Understand Your Impact - Equivalencies */}
      <section id="equivalencies" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Understand Your Impact
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Use our greenhouse gases equivalencies calculator to visualize your carbon footprint in real-world terms
            </p>
          </div>

          {emissions !== null && breakdown ? (
            <>
              <EquivalenciesVisualizer emissions={emissions} className="mb-8" />
              
              <div className="flex justify-center gap-4 mt-8">
                <button
                  onClick={() => setShowSaveModal(true)}
                  className="bg-[#2E8B57] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#267347] transition-colors flex items-center gap-2"
                >
                  <Save className="w-5 h-5" />
                  Save Calculation
                </button>
                <button
                  onClick={() => exportToPDF({
                    title: "Personal Carbon Footprint Report",
                    totalEmissions: emissions,
                    breakdown: breakdown,
                    calculationType: "personal",
                  })}
                  className="bg-white text-[#2E8B57] px-8 py-3 rounded-lg font-semibold border-2 border-[#2E8B57] hover:bg-green-50 transition-colors flex items-center gap-2"
                >
                  <Download className="w-5 h-5" />
                  Export PDF
                </button>
              </div>
            </>
          ) : (
            <div className="text-center py-12 bg-white rounded-2xl border-2 border-dashed border-gray-300">
              <Calculator className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">
                Calculate your emissions above to see real-world equivalencies
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Specialized Calculators Section */}
      <section className="py-16 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Specialized Calculators
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Calculate carbon footprints for specific activities and lifestyles
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Wedding Calculator */}
            <Link
              to="/wedding"
              className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all transform hover:-translate-y-1 border border-gray-200 group"
            >
              <div className="bg-pink-100 w-14 h-14 rounded-lg flex items-center justify-center mb-4 group-hover:bg-pink-200 transition-colors">
                <Heart className="w-8 h-8 text-[#2E8B57]" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Wedding & Events
              </h3>
              <p className="text-gray-600 mb-3">
                Plan a green wedding and calculate emissions from guests, travel, and catering.
              </p>
              <span className="text-[#2E8B57] font-medium group-hover:underline">
                Calculate Wedding Footprint →
              </span>
            </Link>

            {/* Pet Calculator */}
            <Link
              to="/pet"
              className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all transform hover:-translate-y-1 border border-gray-200 group"
            >
              <div className="bg-amber-100 w-14 h-14 rounded-lg flex items-center justify-center mb-4 group-hover:bg-amber-200 transition-colors">
                <Dog className="w-8 h-8 text-[#2E8B57]" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Pet Pawprint 🐾
              </h3>
              <p className="text-gray-600 mb-3">
                Discover your dog's carbon pawprint from food, waste, and supplies.
              </p>
              <span className="text-[#2E8B57] font-medium group-hover:underline">
                Calculate Pet Pawprint →
              </span>
            </Link>

            {/* Fashion Calculator */}
            <Link
              to="/fashion"
              className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all transform hover:-translate-y-1 border border-gray-200 group"
            >
              <div className="bg-purple-100 w-14 h-14 rounded-lg flex items-center justify-center mb-4 group-hover:bg-purple-200 transition-colors">
                <Shirt className="w-8 h-8 text-[#2E8B57]" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Fashion Footprint
              </h3>
              <p className="text-gray-600 mb-3">
                Calculate the environmental impact of your wardrobe and fast fashion habits.
              </p>
              <span className="text-[#2E8B57] font-medium group-hover:underline">
                Calculate Fashion Impact →
              </span>
            </Link>

            {/* Digital Calculator */}
            <Link
              to="/digital"
              className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all transform hover:-translate-y-1 border border-gray-200 group"
            >
              <div className="bg-blue-100 w-14 h-14 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-200 transition-colors">
                <Wifi className="w-8 h-8 text-[#2E8B57]" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Digital Emissions
              </h3>
              <p className="text-gray-600 mb-3">
                Your emails have a carbon footprint! Calculate emissions from streaming and internet use.
              </p>
              <span className="text-[#2E8B57] font-medium group-hover:underline">
                Calculate Digital Footprint →
              </span>
            </Link>

            {/* Student Calculator */}
            <Link
              to="/student"
              className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all transform hover:-translate-y-1 border border-gray-200 group"
            >
              <div className="bg-yellow-100 w-14 h-14 rounded-lg flex items-center justify-center mb-4 group-hover:bg-yellow-200 transition-colors">
                <GraduationCap className="w-8 h-8 text-[#2E8B57]" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                For Students (Ages 10-15)
              </h3>
              <p className="text-gray-600 mb-3">
                Kid-friendly calculator and free teacher worksheet. Learn about climate science!
              </p>
              <span className="text-[#2E8B57] font-medium group-hover:underline">
                Learn & Calculate →
              </span>
            </Link>

            {/* Business Calculator - Highlight it too */}
            <Link
              to="/business"
              className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all transform hover:-translate-y-1 border border-gray-200 group"
            >
              <div className="bg-green-100 w-14 h-14 rounded-lg flex items-center justify-center mb-4 group-hover:bg-green-200 transition-colors">
                <Calculator className="w-8 h-8 text-[#2E8B57]" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Small Business
              </h3>
              <p className="text-gray-600 mb-3">
                Calculate Scope 1 & 2 emissions for your business with free GHG Inventory Template.
              </p>
              <span className="text-[#2E8B57] font-medium group-hover:underline">
                Business Calculator →
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits Section - Greenhouse Gas Reduction */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Start Your Greenhouse Gas Reduction Journey
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our greenhouse gas reduction calculator helps you identify opportunities for savings
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingDown className="w-8 h-8 text-[#2E8B57]" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Identify Reduction Opportunities
              </h3>
              <p className="text-gray-600">
                Discover which activities contribute most to your carbon footprint
              </p>
            </div>

            <div className="text-center">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calculator className="w-8 h-8 text-[#2E8B57]" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Track Your Progress
              </h3>
              <p className="text-gray-600">
                Use our tools regularly to monitor your emission reductions over time
              </p>
            </div>

            <div className="text-center">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Leaf className="w-8 h-8 text-[#2E8B57]" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Make an Impact
              </h3>
              <p className="text-gray-600">
                Join thousands taking action to reduce their environmental impact
              </p>
            </div>
          </div>
        </div>
      </section>

      <SaveCalculationModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onSave={async (name, notes) => {
          if (emissions !== null && breakdown) {
            await saveCalculation({
              calculationType: 'personal',
              totalEmissions: emissions,
              breakdown: breakdown,
              equivalencies: {
                trees: treesNeeded,
                miles: milesDriven
              },
              inputs: {
                commuteDistance,
                commuteMode,
                homeEnergy,
                homeSize,
                energySource,
                dietType
              },
              userName: name,
              notes: notes
            });
          }
        }}
      />

      <HistoryViewer
        isOpen={showHistory}
        onClose={() => setShowHistory(false)}
        calculations={calculations}
        loading={loading}
        onDelete={deleteCalculation}
        onLoad={(calc) => {
          if (calc.inputs) {
            setCommuteDistance(calc.inputs.commuteDistance || '');
            setCommuteMode(calc.inputs.commuteMode || 'car-gasoline');
            setHomeEnergy(calc.inputs.homeEnergy || '');
            setHomeSize(calc.inputs.homeSize || 'medium');
            setEnergySource(calc.inputs.energySource || 'grid-average');
            setDietType(calc.inputs.dietType || 'mixed');
            setEmissions(calc.totalEmissions);
            setBreakdown(calc.breakdown);
          }
        }}
      />
    </div>
  );
}
